import {
  Box,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  ArrowUpRight,
  Calendar,
  DollarSign,
  FileText,
  Hash,
  TrendingUp,
  Users,
} from 'lucide-react';
import React from 'react';
import { useHistory } from 'react-router-dom';
import DefaultPage from '../../components/shared/DefaultPage';
import InfoCard from '../../components/shared/InfoCard';
import { useDashboard } from '../../services/hooks/useDashboard';
import formatValue from '../../utils/formatValue';

interface ICard {
  icon: any;
  titulo: string;
  descricao: string;
  ganho: string;
  gradient: string;
  hoverColor: string;
  path: string;
  dev?: boolean;
  externalUrl?: string;
}

const Card: React.FC<ICard> = ({
  icon,
  titulo,
  descricao,
  ganho,
  gradient,
  hoverColor,
  path,
  dev,
  externalUrl,
}) => {
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');

  const toast = useToast();

  const history = useHistory();

  return (
    <Box
      bg={bg}
      p="8"
      rounded="2xl"
      borderWidth="1px"
      cursor="pointer"
      transition="background 0.5s ease"
      _hover={{
        bg: hoverColor,
      }}
      onClick={() => {
        if (externalUrl) {
          window.open(externalUrl, '_blank');
          return;
        }

        if (dev) {
          toast({
            title: 'Em breve',
            description: 'Funcionalidade em desenvolvimento',
            position: 'bottom-right',
            duration: 600,
            isClosable: true,
          });
        } else {
          history.push(path);
        }
      }}
      role="group"
    >
      <Flex justify="space-between">
        <Box
          bgGradient={gradient}
          p="4"
          rounded="xl"
          //
        >
          {icon}
        </Box>
        <Box
          transition="transform 0.3s ease"
          _groupHover={{
            transform: 'scale(1.4)',
          }}
        >
          <ArrowUpRight color={color} size="40px" />
        </Box>
      </Flex>
      <Heading fontSize="3xl" mt="4">
        {titulo}
      </Heading>
      <Heading fontSize="lg" fontWeight="small" color={color} mt="2">
        {descricao}
      </Heading>
      <Divider my="3" />
      <Flex color="white" justify="space-between" align="center">
        <Text color={color} fontWeight="medium" fontSize="lg" />
        <Text
          bgGradient={gradient}
          bgClip="text"
          fontWeight="bold"
          fontSize="lg"
        >
          {ganho}
        </Text>
      </Flex>
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');
  const bgGreen = useColorModeValue(
    'rgba(0,250,0,0.2)',
    'rgba(150,250,150,0.04)',
  );
  const bgBlue = useColorModeValue(
    'rgba(0, 120, 255, 0.2)',
    'rgba(120, 180, 255, 0.04)',
  );

  const bgOrange = useColorModeValue(
    'rgba(255, 140, 0, 0.2)',
    'rgba(255, 190, 120, 0.04)',
  );

  const bgPurple = useColorModeValue(
    'rgba(160, 90, 255, 0.2)',
    'rgba(200, 160, 255, 0.04)',
  );

  const { data, isLoading } = useDashboard();

  return (
    <DefaultPage>
      <Flex align="center" flexDir="column">
        <Heading fontSize="6xl">
          Como vamos{' '}
          <Heading
            fontSize="6xl"
            display="inline"
            bgGradient="linear(to-b, green.400, green.600)"
            bgClip="text"
          >
            lucrar hoje
          </Heading>
          ?
        </Heading>
        <Heading fontSize="2xl" fontWeight="medium" color="gray.500">
          Escolha uma opção abaixo e comece a aumentar seus ganhos
        </Heading>
      </Flex>
      <SimpleGrid
        templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
        gridColumnGap="4"
        gridRowGap="4"
        mx="4"
        mt="4"
        // mb="6"
      >
        <InfoCard
          titulo="Saldo Disponível"
          valor={formatValue(data?.saldo_disponivel || 0)}
          icon={DollarSign}
          cor="green.500"
          isLoading={isLoading}
        />
        <InfoCard
          titulo="Total Ganho"
          valor={formatValue(data?.total_ganho || 0)}
          icon={TrendingUp}
          cor="blue.500"
          isLoading={isLoading}
        />
        <InfoCard
          titulo="Nº Vendas do mês"
          valor={data?.numero_vendas || 0}
          icon={Calendar}
          cor="purple.500"
          isLoading={isLoading}
        />
      </SimpleGrid>
      <SimpleGrid
        columns={[1, 1, 2, 2]}
        spacing="20px"
        my="4"
        mx="4"
        // bg=""
      >
        <Card
          ganho="Indique já!"
          // ganho="10-30% recorrente"
          icon={<TrendingUp size="40px" />}
          titulo="Vender Sistema ERP"
          descricao="Venda nossos sistemas e ganhe comissão recorrente mensal"
          gradient="linear(to-b, green.400, teal.600)"
          hoverColor={bgGreen}
          path="/vendas-erp"
          externalUrl="https://api.whatsapp.com/send?phone=5546988130055&text=Ol%C3%A1!%20Vim%20pelo%20sistema%20Parceiros%20e%20gostaria%20de%20mais%20detalhes%20sobre%20a%20venda%20do%20sistema%20ERP."
          dev
        />
        <Card
          ganho="Comece já!"
          icon={<FileText size="40px" />}
          titulo="Vender Certificado Digital"
          descricao="Venda certificados digitais e ganhe comissão por venda"
          gradient="linear(to-b, blue.400, blue.700)"
          hoverColor={bgBlue}
          path="/certificados"
        />
        <Card
          ganho="Em breve"
          // ganho="Saldo: R$ 2847.50"
          icon={<DollarSign size="40px" />}
          titulo="Solicitar Saque"
          descricao="Retire seus ganhos de forma rápida e segura"
          gradient="linear(to-b, orange.300, orange.700)"
          hoverColor={bgOrange}
          path="/meus-clientes"
          dev
        />
        <Card
          ganho="Em breve"
          // ganho="Histórico Completo"
          icon={<Users size="40px" />}
          titulo="Meus Clientes"
          descricao="Acompanhe todas as suas vendas e comissões"
          gradient="linear(to-b, purple.400, purple.600)"
          hoverColor={bgPurple}
          path="/saques"
          dev
        />
      </SimpleGrid>
    </DefaultPage>
  );
};

export default Dashboard;
